import { TFunction } from '@lyearn/i18n';
import { PeopleOutline, PersonIndirectReporteeOutline } from '@lyearn/icons';
import { ActionMenuItemProps } from '@lyearn/molecules';
import {
  FilterManager,
  SingleSelectItem,
  SortAndFilterItem,
} from '@/components/FilterManager/types';
import { FilterOperatorEnum, Maybe, ReporteeFilterTypeEnum, UserType } from '@/types/schema';

const REPORTEE_FILTER_ITEMS = [
  {
    id: ReporteeFilterTypeEnum.DirectReportee,
    labelKey: 'Common:Filter.DirectReportees',
    Icon: PersonIndirectReporteeOutline,
  },
  {
    id: ReporteeFilterTypeEnum.InDirectReportee,
    labelKey: 'Common:Filter.AllReportees',
    Icon: PeopleOutline,
  },
];

export const getReporteeFilterItems = (t: TFunction): SingleSelectItem[] =>
  REPORTEE_FILTER_ITEMS.map(({ Icon, id, labelKey }) => ({
    id,
    icon: <Icon className="h-20 w-20" />,
    label: t(labelKey),
    value: id,
  }));

export const getReporteeFilterItem = (t: TFunction): SortAndFilterItem => ({
  type: FilterManager.SingleSelectFilter,
  id: 'reporteeFilterType',
  defaultPlaceholder: '',
  defaultSelectedValue: ReporteeFilterTypeEnum.DirectReportee,
  isEnabled: true,
  hideOperator: true,
  appearance: 'primary',
  items: getReporteeFilterItems(t),
});

export const getDividerFilterItem = (): SortAndFilterItem => ({
  type: FilterManager.Divider,
  isEnabled: true,
});

export const getReporteeFilterActionMenuItems = (t: TFunction): ActionMenuItemProps[] =>
  REPORTEE_FILTER_ITEMS.map(({ Icon, id, labelKey }) => ({
    id,
    StartIcon: Icon,
    label: t(labelKey),
  }));

export const getUserReporteeIds = (reportees?: UserReportees) => {
  return reportees?.map(({ id }) => id) || [];
};

type UserReportees = Maybe<Pick<UserType, 'id'>[]>;

const REPORTEE_TYPE_PROPERTY_CONFIG = {
  [ReporteeFilterTypeEnum.DirectReportee]: 'user_manager',
  [ReporteeFilterTypeEnum.InDirectReportee]: 'user_indirect_manager',
};

const ACTIVITIES_REPORTEE_TYPE_PROPERTY_CONFIG = {
  [ReporteeFilterTypeEnum.DirectReportee]: 'manager_ids_for_activity_metrics',
  [ReporteeFilterTypeEnum.InDirectReportee]: 'indirect_manager_ids_for_activity_metrics',
};

const REPORTEE_TYPE_USER_FILTER_CONFIG = {
  [ReporteeFilterTypeEnum.DirectReportee]: 'parentId' as const,
  [ReporteeFilterTypeEnum.InDirectReportee]: 'hierarchyPath' as const,
};

export const getTeamActivitiesReporteeFilterItem = (
  reporteeFilterType: ReporteeFilterTypeEnum,
  userId = 'ly__userId_me',
) => {
  return {
    property: ACTIVITIES_REPORTEE_TYPE_PROPERTY_CONFIG[reporteeFilterType],
    operator: FilterOperatorEnum.In,
    value: [userId],
  };
};

export const getTeamDashboardReporteeFilterItem = (
  reporteeFilterType: ReporteeFilterTypeEnum,
  userId = 'ly__userId_me',
) => {
  return {
    property: REPORTEE_TYPE_PROPERTY_CONFIG[reporteeFilterType],
    operator: FilterOperatorEnum.In,
    value: [userId],
  };
};

interface Params {
  currentUserId: string;
  reporteeFilterType: ReporteeFilterTypeEnum;
}

interface ReturnType {
  parentId?: string;
  hierarchyPath?: string;
}

export const getUserFiltersForReporteeFilter = (params: Params): ReturnType => {
  const { currentUserId, reporteeFilterType } = params;
  const key = REPORTEE_TYPE_USER_FILTER_CONFIG[reporteeFilterType];

  return { [key]: currentUserId };
};

export const getReporteeTypeFromFilter = (state: any): ReporteeFilterTypeEnum => {
  return state.filter.reporteeFilterType || ReporteeFilterTypeEnum.DirectReportee;
};
